import { Dispatch, memo, SetStateAction, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
// import Icon from 'components/icon/Icon';
import { cn } from 'lib/utils';
import { ScrollArea } from 'components/ui/scroll-area';
import LineChart from 'components/charts/LineChart';
import {
  ColorRange,
  ParameterHistory,
  ParameterHistoryParams,
} from 'services/face-scan/type';
import {
  CategorizedReadingData,
  ReadingData,
  ReadingDataWithSubParameters,
} from '../../../types';

import NoData from 'assets/no-data.png';
import ReportActions from './ReportActions';
import Loader from 'components/Loader';
import useAuthStore from 'store/auth';
import { useMediaPredicate } from 'react-media-hook';
import ScanVitalDetail from './ScanVitalDetail';

interface ScanDetailProps {
  data: CategorizedReadingData;
  scanData?: ReadingDataWithSubParameters[];
  className?: string;
  containerClassName?: string;
  readingId?: string;
  isLoading?: boolean;
  isParameterFetching?: boolean;
  historyParams?: ParameterHistoryParams;
  refetchScanReports?: () => void;
  setHistoryParams?: Dispatch<SetStateAction<ParameterHistoryParams>>;
}

interface HistoryData {
  data: ParameterHistory[];
  colorRanges: ColorRange[];
}

const ScanDetail = ({
  data,
  scanData,
  className,
  containerClassName,
  readingId,
  isLoading,
  isParameterFetching,
  historyParams,
  refetchScanReports,
  setHistoryParams,
}: ScanDetailProps) => {
  const { t } = useTranslation();
  const isMediaMobile = useMediaPredicate('(max-width: 640px)');

  const [searchParams, setSearchParams] = useSearchParams();
  const vitalKey = searchParams.get('vital') || '';

  const queryClient = useQueryClient();
  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const parameterHistory =
    queryClient.getQueryData(['parameter-history', profileId, historyParams]) ||
    null;

  const [historyData, setHistoryData] = useState<HistoryData>({
    data: [],
    colorRanges: [],
  });
  const [bpHistoryData, setBpHistoryData] = useState<
    Record<'diastolic' | 'systolic', HistoryData>
  >({
    diastolic: { data: [], colorRanges: [] },
    systolic: { data: [], colorRanges: [] },
  });
  const [selectedVital, setSelectedVital] = useState<ReadingData>();

  const historyOptions = [
    { label: t('last_7'), value: 'last_7' },
    { label: t('last_5'), value: 'last_5' },
    { label: t('last_2'), value: 'last_2' },
    { label: t('this_month'), value: 'this_month' },
    { label: t('this_quarter'), value: 'this_quarter' },
  ];

  const onVitalSelect = (vitalKey: string) => {
    if (selectedVital && selectedVital.key === vitalKey) {
      setSelectedVital(undefined);
      setHistoryData({ data: [], colorRanges: [] });
      return;
    }

    const vital = scanData?.find((data) => data.key === vitalKey);
    setSelectedVital(vital);
  };

  const onHistoryFilter = (value: string) => {
    setHistoryParams?.({ type: value });
  };

  const historyValue = () =>
    (historyParams &&
      historyOptions.find((opt) => opt.value === historyParams.type)?.label) ||
    '';

  useEffect(() => {
    if (vitalKey && scanData && scanData.length) {
      onVitalSelect(vitalKey);
      if (!isMediaMobile) setSearchParams({});
    }
  }, [vitalKey, scanData, isMediaMobile]);

  useEffect(() => {
    if (parameterHistory && selectedVital?.key) {
      if (selectedVital.key === 'BLOOD_PRESSURE') {
        setHistoryData({ data: [], colorRanges: [] });
        const systolicHistory = (parameterHistory as any)['systolic'];
        const diastolicHistory = (parameterHistory as any)['diastolic'];

        setBpHistoryData({
          systolic: {
            data: systolicHistory ? [...systolicHistory].reverse() : [],
            colorRanges: selectedVital.colorRange ?? [],
          },
          diastolic: {
            data: diastolicHistory ? [...diastolicHistory].reverse() : [],
            colorRanges: selectedVital.colorRange ?? [],
          },
        });
      } else {
        setBpHistoryData({
          systolic: { data: [], colorRanges: [] },
          diastolic: { data: [], colorRanges: [] },
        });
        const history =
          parameterHistory[selectedVital.key as keyof typeof parameterHistory];

        setHistoryData({
          data: history ? [...history].reverse() : [],
          colorRanges:
            history && selectedVital?.colorRange
              ? selectedVital.colorRange
              : [],
        });
      }
    }
  }, [selectedVital, parameterHistory]);

  if (isLoading) {
    return <Loader />;
  }

  if (!data || !Object.keys(data)?.length) {
    return (
      <div className='flex flex-col items-center justify-center h-full lg:border rounded-2xl border-primary'>
        <div className='size-40'>
          <img
            src={NoData}
            alt='no data'
            className='object-cover w-full h-full'
          />
        </div>
        <p>{t('no_scan_data')}</p>
      </div>
    );
  }

  const historyFilter = () => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger>
          <p className='text-sm font-medium text-primary'>
            {t('range')}: {historyValue()}
          </p>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {historyOptions.map(({ label, value }) => (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onHistoryFilter(value);
              }}
              key={value}
            >
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <>
      {readingId ? (
        <ReportActions
          readingId={readingId}
          scanData={scanData}
          refetchScanReports={refetchScanReports}
        />
      ) : null}

      <div
        className={cn(
          'lg:border rounded-2xl border-primary',
          containerClassName
        )}
        id='vital-signs'
      >
        <div className='py-6 lg:pl-6'>
          <div className='grid grid-cols-12'>
            <ScrollArea
              className={cn(
                'transition-all duration-300',
                selectedVital ? 'col-span-7 pr-4' : 'col-span-12 pr-6',
                className
              )}
            >
              <div className='flex flex-col space-y-8'>
                {Object.keys(data)?.map((category) => {
                  return (
                    <div key={category}>
                      <h2 className='text-xl font-semibold'>{category}</h2>

                      {data[category]?.length ? (
                        <div className='p-4 mt-2 ml-2 border-l-2 border-l-primary'>
                          {data[category]?.map((vital) => (
                            <ScanVitalDetail
                              vital={vital}
                              onSelect={onVitalSelect}
                              key={vital.key}
                              selectedVital={selectedVital}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <ScrollArea
              className={cn(
                'col-span-5 lg:pl-4 lg:border-l border-dashed border-l-gray-400 lg:pr-6',
                selectedVital ? 'block' : 'hidden',
                className
              )}
            >
              <Accordion type='multiple' defaultValue={['info']}>
                <AccordionItem value='history' className='relative'>
                  <AccordionTrigger>
                    <div className='flex items-center justify-between'>
                      <p>{t('parameter_history')}</p>

                      <div className='absolute right-0 hidden xl:block top-4'>
                        {historyFilter()}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className='flex justify-end mb-2 mr-1 xl:hidden'>
                      {historyFilter()}
                    </div>
                    <div
                      className={cn(
                        'w-full h-40 p-4 border lg:h-48 border-primary rounded-3xl',
                        selectedVital?.key === 'BLOOD_PRESSURE' && 'lg:h-96'
                      )}
                    >
                      {isParameterFetching ? (
                        <div className='grid w-full h-full place-items-center'>
                          <Loader2 className='stroke-1 animate-spin size-6 text-primary' />
                        </div>
                      ) : (
                        <>
                          {selectedVital?.key === 'BLOOD_PRESSURE' ? (
                            <>
                              {bpHistoryData.diastolic.data.length &&
                              bpHistoryData.systolic.data.length ? (
                                Object.keys(bpHistoryData).map((bp) => (
                                  <div key={bp}>
                                    <LineChart
                                      {...bpHistoryData[
                                        bp as keyof typeof bpHistoryData
                                      ]}
                                    />
                                    <p className='font-medium text-center capitalize'>
                                      {bp}
                                    </p>
                                  </div>
                                ))
                              ) : (
                                <div className='grid w-full h-full place-items-center'>
                                  <p className='font-medium text-muted-foreground'>
                                    {t('no_history')}
                                  </p>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              {historyData?.data?.length ? (
                                <LineChart {...historyData} />
                              ) : (
                                <div className='grid w-full h-full place-items-center'>
                                  <p className='font-medium text-muted-foreground'>
                                    {t('no_history')}
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
                {selectedVital?.colorRangeWithCategory?.length ? (
                  <AccordionItem value='ranges'>
                    <AccordionTrigger>{t('parameter_ranges')}</AccordionTrigger>
                    <AccordionContent>
                      {selectedVital?.colorRangeWithCategory?.map(
                        ({ color, range, eng_category }, index) => {
                          return (
                            <div
                              key={index}
                              className={cn(
                                'flex items-center justify-between py-2 font-medium border-b',
                                index === 0 && 'border-t'
                              )}
                            >
                              <div className='flex items-center gap-x-2'>
                                <div
                                  className='w-4 h-4 rounded-full'
                                  style={{ backgroundColor: color }}
                                />
                                <p className='text-sm'>{eng_category}</p>
                              </div>

                              <div>
                                {range.length > 1
                                  ? `${range[0]} - ${range[1]}`
                                  : String(range[0])
                                      .replace('>', 'Higher than ')
                                      .replace('<', 'Lower than ')
                                      .replace('=', 'and equal to ')}
                              </div>
                            </div>
                          );
                        }
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ) : null}
                <AccordionItem value='info'>
                  <AccordionTrigger>{t('parameter_info')}</AccordionTrigger>
                  <AccordionContent>
                    <div className='space-y-4 font-medium'>
                      <p className='text-primary'>
                        {selectedVital?.short_intro}
                      </p>

                      <div
                        className='whitespace-pre-wrap'
                        style={{ wordBreak: 'break-word' }}
                      >
                        {selectedVital
                          ? parse(
                              selectedVital?.long_intro
                                .replace(/<link>/g, '<a>')
                                .replace(/<\/link>/g, '</a>'),
                              {
                                replace(domNode: any) {
                                  const text = domNode?.data;
                                  if (!text) return null;

                                  if (text?.startsWith('https://')) {
                                    return (
                                      <a
                                        key={text}
                                        href={text}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='text-blue-500 '
                                      >
                                        {text}
                                      </a>
                                    );
                                  }
                                },
                              }
                            )
                          : null}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </ScrollArea>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(ScanDetail);
